import React, { useState, useEffect, useCallback } from 'react';
import { X, ShoppingCart, Plus, Minus, Trash2, Loader, TriangleAlert as AlertTriangle, ShoppingBag } from 'lucide-react';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { CART_VIEW_URL, CART_UPDATE_URL, CART_SUMMARY_URL, CART_REMOVE_ITEM_URL, CART_CLEAR_URL } from '../../config/api';
import CartCheckout from './CartCheckout';
import './Cart.css';

interface CartItem {
  cart_id: number;
  food_id: number;
  food_name: string;
  price: number;
  quantity: number;
  total: number;
}

interface CartSummary {
  total_items: number;
  subtotal: number;
  delivery_fee: number;
  tax: number;
  grand_total: number;
}

interface CartProps {
  isOpen: boolean;
  onClose: () => void;
  onCartChange: () => void;
  onCheckoutSuccess: (data: any) => void;
}

const Cart: React.FC<CartProps> = ({ isOpen, onClose, onCartChange, onCheckoutSuccess }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [summary, setSummary] = useState<CartSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [clearing, setClearing] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cartRes, summaryRes] = await Promise.all([
        authenticatedFetch(CART_VIEW_URL),
        authenticatedFetch(CART_SUMMARY_URL),
      ]);
      const cartJson = await cartRes.json();
      const summaryJson = await summaryRes.json();
      if (cartRes.ok && cartJson.success) {
        setItems(cartJson.items || []);
      } else {
        setError('Failed to load cart.');
      }
      if (summaryRes.ok && summaryJson.success) {
        setSummary(summaryJson);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchCart();
  }, [isOpen, fetchCart]);

  const handleUpdateQuantity = async (cartId: number, newQty: number) => {
    if (newQty < 1) return;
    setUpdatingId(cartId);
    try {
      const res = await authenticatedFetch(CART_UPDATE_URL(cartId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQty }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        await fetchCart();
        onCartChange();
      } else {
        setError(json.message || 'Failed to update quantity.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRemoveItem = async (cartId: number) => {
    setUpdatingId(cartId);
    try {
      const res = await authenticatedFetch(CART_REMOVE_ITEM_URL(cartId), { method: 'DELETE' });
      const json = await res.json();
      if (res.ok && json.success) {
        setItems(prev => prev.filter(i => i.cart_id !== cartId));
        onCartChange();
        await fetchCart();
      } else {
        setError(json.message || 'Failed to remove item.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleClearCart = async () => {
    setClearing(true);
    setError(null);
    try {
      const res = await authenticatedFetch(CART_CLEAR_URL, { method: 'DELETE' });
      const json = await res.json();
      if (res.ok && json.success) {
        setItems([]);
        setSummary(null);
        onCartChange();
      } else {
        setError(json.message || 'Failed to clear cart.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  const handleCheckoutSuccess = (data: any) => {
    setShowCheckout(false);
    setItems([]);
    setSummary(null);
    onCartChange();
    onClose();
    onCheckoutSuccess(data);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="cart-overlay" onClick={onClose} />
      <div className="cart-drawer">
        <div className="cart-header">
          <div className="cart-title">
            <ShoppingCart size={20} />
            <span>Your Cart</span>
            {items.length > 0 && (
              <span className="cart-item-count">{items.reduce((s, i) => s + i.quantity, 0)}</span>
            )}
          </div>
          <div className="cart-header-actions">
            {items.length > 0 && (
              <button
                className="cart-clear-btn"
                onClick={handleClearCart}
                disabled={clearing || loading}
                title="Clear cart"
              >
                {clearing ? <Loader size={14} className="cart-spinner" /> : <Trash2 size={14} />}
                Clear
              </button>
            )}
            <button className="cart-close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="cart-body">
          {loading && (
            <div className="cart-loading">
              <Loader size={28} className="cart-spinner" />
              <span>Loading cart...</span>
            </div>
          )}

          {error && !loading && (
            <div className="cart-error">
              <AlertTriangle size={18} />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="cart-empty">
              <ShoppingBag size={52} />
              <p>Your cart is empty</p>
              <span>Browse kitchens and add items to get started</span>
            </div>
          )}

          {!loading && items.length > 0 && (
            <ul className="cart-items-list">
              {items.map(item => (
                <li key={item.cart_id} className="cart-item">
                  <div className="cart-item-info">
                    <p className="cart-item-name">{item.food_name}</p>
                    <p className="cart-item-price">₹{item.price.toFixed(2)} each</p>
                  </div>
                  <div className="cart-item-controls">
                    <div className="cart-qty-controls">
                      <button
                        className="cart-qty-btn"
                        onClick={() => handleUpdateQuantity(item.cart_id, item.quantity - 1)}
                        disabled={updatingId === item.cart_id || item.quantity <= 1}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="cart-qty-value">
                        {updatingId === item.cart_id ? (
                          <Loader size={12} className="cart-spinner" />
                        ) : (
                          item.quantity
                        )}
                      </span>
                      <button
                        className="cart-qty-btn"
                        onClick={() => handleUpdateQuantity(item.cart_id, item.quantity + 1)}
                        disabled={updatingId === item.cart_id}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <p className="cart-item-total">₹{item.total.toFixed(2)}</p>
                    <button
                      className="cart-remove-btn"
                      onClick={() => handleRemoveItem(item.cart_id)}
                      disabled={updatingId === item.cart_id}
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {!loading && items.length > 0 && summary && (
          <div className="cart-footer">
            <div className="cart-summary">
              <div className="cart-summary-row">
                <span>Subtotal</span>
                <span>₹{summary.subtotal.toFixed(2)}</span>
              </div>
              <div className="cart-summary-row">
                <span>Delivery fee</span>
                <span>₹{summary.delivery_fee.toFixed(2)}</span>
              </div>
              <div className="cart-summary-row">
                <span>Tax</span>
                <span>₹{summary.tax.toFixed(2)}</span>
              </div>
              <div className="cart-summary-row cart-summary-total">
                <span>Total</span>
                <span>₹{summary.grand_total.toFixed(2)}</span>
              </div>
            </div>
            <button className="cart-checkout-btn" onClick={() => setShowCheckout(true)}>
              Proceed to Checkout — ₹{summary.grand_total.toFixed(2)}
            </button>
          </div>
        )}
      </div>

      {showCheckout && (
        <CartCheckout
          summary={summary}
          onClose={() => setShowCheckout(false)}
          onSuccess={handleCheckoutSuccess}
        />
      )}
    </>
  );
};

export default Cart;
